<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { Button, Icon, Text, Modal, Container } from '@Shelly/shelly-design-system/index';
	import { rgbToCss } from '../../helpers/radar-helper';
	import { format } from 'svelte-i18n';
	import type { Zone } from '../../Types';

	export let zone: Zone;
	export let isMainZone: boolean = false;
	export let personCount: number = 0;
	/** Local-only: whether the zone is shown on the grid (not persisted). */
	export let visible: boolean = true;

	const dispatch = createEventDispatcher();

	let showDeleteConfirmation = false;

	$: selectedColor = zone.color || [255, 255, 255];

	function handleEdit() {
		dispatch('edit', zone);
	}

	function handleEditSettings() {
		dispatch('editSettings', zone);
	}

	function requestDelete() {
		showDeleteConfirmation = true;
	}

	function confirmDelete() {
		showDeleteConfirmation = false;
		dispatch('delete', zone);
	}

	function toggleVisible() {
		dispatch('toggleVisible', zone);
	}
</script>

<div class="zone-row">
	{#if isMainZone}
		<span class="zone-row__icon" style="color: {rgbToCss(selectedColor)};" aria-hidden="true">
			<i class="fas fa-circle"></i>
		</span>
	{:else}
		<button
			class="zone-row__icon zone-row__icon--btn"
			style="color: {rgbToCss(selectedColor)};"
			on:click={toggleVisible}
			aria-label={visible
				? $format('presence_zone_hide', { default: 'Hide zone on grid' })
				: $format('presence_zone_show', { default: 'Show zone on grid' })}
			title={visible
				? $format('presence_zone_hide', { default: 'Hide zone on grid' })
				: $format('presence_zone_show', { default: 'Show zone on grid' })}
		>
			{#if visible}
				<i class="fas fa-eye"></i>
			{:else}
				<i class="fas fa-eye-slash"></i>
			{/if}
		</button>
	{/if}

	<span class="zone-row__name">{zone.name}</span>

	{#if personCount > 0}
		<span
			class="zone-row__people"
			aria-label={personCount === 1
				? $format('presence_zone_persons_detected', {
						values: { count: personCount },
						default: `${personCount} person detected`,
					})
				: $format('presence_zone_people_detected', {
						values: { count: personCount },
						default: `${personCount} people detected`,
					})}
		>
			<i class="fas fa-person-simple" aria-hidden="true"></i>
			{personCount}
		</span>
	{/if}

	<div class="zone-row__actions">
		{#if isMainZone}
			<button
				class="zone-row__btn"
				on:click={handleEditSettings}
				aria-label={$format('presence_zone_room_settings', { default: 'Room settings' })}
				title={$format('presence_zone_room_settings', { default: 'Room settings' })}
			>
				<i class="fas fa-pencil" aria-hidden="true"></i>
			</button>
		{:else}
			<button
				class="zone-row__btn"
				on:click={handleEdit}
				aria-label={$format('presence_zone_edit_zone', { default: 'Edit zone' })}
				title={$format('presence_zone_edit_zone', { default: 'Edit zone' })}
			>
				<i class="fas fa-pencil" aria-hidden="true"></i>
			</button>
			{#if !zone.readOnly}
				<button
					class="zone-row__btn zone-row__btn--danger"
					on:click={requestDelete}
					aria-label={$format('presence_zone_delete_zone', { default: 'Delete zone' })}
					title={$format('presence_zone_delete_zone', { default: 'Delete zone' })}
				>
					<i class="fas fa-trash" aria-hidden="true"></i>
				</button>
			{/if}
		{/if}
	</div>
</div>

<!-- Delete confirmation modal -->
<Modal
	showModal={showDeleteConfirmation}
	on:close={() => (showDeleteConfirmation = false)}
	title={$format('label_delete_zone', { default: 'Delete Zone' })}
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<div class="d-flex flex-direction-column align-items-center">
				<Icon class="mb-xs" icon="text-danger fa-light fa-trash" size="xxl" />
				<Text class="text-bold text-center mb-s" styleType="controls-large">
					{zone.name}
				</Text>
				<Text class="align-self-center">
					{$format('confirm_delete_zone_msg', {
						values: { zone_name: zone.name },
						default: "Are you sure you want to delete the zone '{zone_name}'?",
					})}
				</Text>
			</div>
		</Container>
	</svelte:fragment>
	<div class="d-flex justify-content-end" slot="footer">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{ sm: 'm' }}
			variant="secondary"
			on:click={() => (showDeleteConfirmation = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{ sm: 'm' }}
			on:click={confirmDelete}
		>
			{$format('button_delete', { default: 'Delete' })}
		</Button>
	</div>
</Modal>

<style scoped>
	.zone-row {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 14px;
		background-color: #1e2023;
		border-radius: 10px;
		margin-bottom: 4px;
	}

	.zone-row__icon {
		font-size: 20px;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		width: 24px;
		justify-content: center;
	}

	.zone-row__icon--btn {
		background: transparent;
		border: none;
		padding: 0;
		cursor: pointer;
		border-radius: 4px;
		transition: opacity 0.12s;
	}

	.zone-row__icon--btn:hover {
		opacity: 0.7;
	}

	.zone-row__name {
		flex: 1;
		font-size: 15px;
		font-weight: 400;
		color: #eaeaea;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.zone-row__people {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 12px;
		font-weight: 600;
		color: #eaeaea;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 999px;
		padding: 2px 8px 2px 6px;
		flex-shrink: 0;
	}

	.zone-row__actions {
		display: flex;
		gap: 4px;
		flex-shrink: 0;
	}

	.zone-row__btn {
		width: 32px;
		height: 32px;
		border-radius: 8px;
		border: none;
		background: transparent;
		color: #a1a2a2;
		font-size: 14px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition:
			background-color 0.12s,
			color 0.12s;
	}

	.zone-row__btn:hover {
		background-color: rgba(255, 255, 255, 0.1);
		color: #eaeaea;
	}

	.zone-row__btn--danger:hover {
		background-color: rgba(220, 53, 69, 0.15);
		color: #dc3545;
	}

	.zone-row__btn:focus-visible {
		outline: 2px solid #2491ff;
		outline-offset: 1px;
	}
</style>
